import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { HotelItinerary } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon, Popover, PopoverContent, PopoverTrigger } from "@tenant/ui";
import clsx from "clsx";
import styles from "./CheckoutBreakdown.module.scss";
import { useCheckoutBreakdown } from "./useCheckoutBreakdown";
import { BonusEarnPill } from "@/modules/wallet";

interface CheckoutBreakdownProps {
  className?: string;
  reservation: HotelItinerary;
  /**
   * Shows the "Earned 3X points" bonus pill next to Amount paid. Only the
   * booking confirmation screen sets this (RBC-2237) — the itinerary carries
   * no card BIN, so eligibility is resolved from checkout state by the caller.
   */
  showBonusEarnPill?: boolean;
}

export const CheckoutBreakdown = ({
  reservation: itinerary,
  className,
  showBonusEarnPill = false,
}: CheckoutBreakdownProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "CheckoutBreakdown");
  const data = useCheckoutBreakdown(itinerary);

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>{t("checkoutBreakdown")}</h3>

      <div className={cn("divider")} />

      <div className={cn("room-summary")}>
        <div className={cn("room-info")}>
          <span className={cn("room-name")}>{data.roomLabel}</span>
          {data.guestSummary !== "" && (
            <span className={cn("room-guests")}>{data.guestSummary}</span>
          )}
          {data.isRefundable && !data.hasCfar && (
            <span className={cn("room-refundable")}>
              {data.cancellationPrimaryText}
            </span>
          )}
        </div>
        {data.image != null && (
          <div className={cn("room-image")}>
            <img src={data.image} alt={data.roomLabel} />
          </div>
        )}
      </div>

      <div className={cn("divider")} />

      <div className={cn("line-items")}>
        <div className={cn("line")}>
          <span>
            {data.roomCount > 1
              ? t("hotelTrips.roomsAndNights", {
                  rooms: t("hotelTrips.room", { count: data.roomCount }),
                  nights: t("nights", { count: data.nightCount }),
                })
              : t("nights", { count: data.nightCount })}
          </span>
          <span>{data.subtotal}</span>
        </div>
        {data.hasCfar && data.cfarPrice && (
          <div className={cn("line")}>
            <div className={cn("cfar-label")}>
              {t("selfServe.cancelForAnyReason")}
              <div className={cn("incl-taxes-subtitle")}>
                {t("core-checkout.priceBreakdown.inclTaxes")}
              </div>
            </div>
            <span>{data.cfarPrice}</span>
          </div>
        )}
        <Popover>
          <PopoverTrigger className={cn("taxes-trigger")}>
            <div className={cn("line")}>
              <span className={cn("taxes-label")}>
                {t("taxesAndFees")}
                <Icon
                  className={cn("taxes-icon")}
                  iconName={IconNameEnum.infoFilled}
                />
              </span>
              <span>{data.taxes}</span>
            </div>
          </PopoverTrigger>
          <PopoverContent className={cn("taxes-tooltip")}>
            {t("core-checkout.priceBreakdown.taxesAndFeesTooltip")}
          </PopoverContent>
        </Popover>
      </div>

      <div className={cn("divider")} />

      <div className={cn("line", ["total"])}>
        <span>{t("total")}</span>
        <span>{data.total}</span>
      </div>

      {data.offerAmount != null && (
        <div className={cn("line")}>
          <span>{t("offerApplied")}</span>
          <span className={cn("credit-value")}>{data.offerAmount}</span>
        </div>
      )}

      {data.travelCreditApplied != null && (
        <div className={cn("line")}>
          <span>{t("trips.travelCreditsApplied")}</span>
          <span className={cn("credit-value")}>{data.travelCreditApplied}</span>
        </div>
      )}

      {data.pointsRedeemed != null && (
        <div className={cn("line")}>
          <span>
            {t("trips.redeemedPoints", { points: data.pointsRedeemed })}
          </span>
          <span className={cn("credit-value")}>{data.pointsRedeemedFiat}</span>
        </div>
      )}

      <div className={cn("divider")} />

      <div className={cn("line", ["total"])}>
        <span>{t("hotelTrips.amountPaid")}</span>
        <span className={cn("amount-paid")}>
          {showBonusEarnPill && <BonusEarnPill variant="earned" />}
          {data.amountPaid}
        </span>
      </div>
      <div className={cn("line")}>
        <span>{t("tripsPaymentMethodTitle")}</span>
        <span>{data.paymentMethod}</span>
      </div>

      {data.hasPayAtProperty && (
        <div className={cn("pay-at-property")}>
          <span className={cn("pay-at-property-title")}>
            {t("lodgingPriceBreakdown.payAtProperty")}
          </span>
          {data.resortFees != null && (
            <div className={cn("pay-at-property-line")}>
              <span>{t("lodgingPriceBreakdown.resortFees")}</span>
              <span>{data.resortFees}</span>
            </div>
          )}
          {data.mandatoryFee != null && (
            <div className={cn("pay-at-property-line")}>
              <span>{t("lodgingPriceBreakdown.mandatoryFee")}</span>
              <span>{data.mandatoryFee}</span>
            </div>
          )}
          {data.mandatoryTax != null && (
            <div className={cn("pay-at-property-line")}>
              <span>{t("lodgingPriceBreakdown.mandatoryTax")}</span>
              <span>{data.mandatoryTax}</span>
            </div>
          )}
          <div className={cn("pay-at-property-divider")} />
          {data.feeTotal != null && (
            <div className={cn("pay-at-property-total")}>
              <span>{t("lodgingPriceBreakdown.totalDueAtCheckIn")}</span>
              <span>{data.feeTotal}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
